import React, { useState, useEffect } from 'react';
import ApiClient, { AvailablePermission } from '../api/client';
import { ModalShell } from './ui';

interface ApiKeyDialogProps {
  apiClient: ApiClient;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description?: string;
    role?: 'user' | 'admin';
    expiresIn?: number;
    permissions?: string[];
  }) => void;
}

const ApiKeyDialog: React.FC<ApiKeyDialogProps> = ({ apiClient, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [expiresIn, setExpiresIn] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permissions picker state
  const [availablePermissions, setAvailablePermissions] = useState<AvailablePermission[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .listAvailablePermissions()
      .then((list) => {
        if (!cancelled) {
          setAvailablePermissions(list);
          setPermissionsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPermissionsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const togglePermission = (key: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Group permissions by category for nicer display
  const grouped = availablePermissions.reduce<Record<string, AvailablePermission[]>>((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter an API key name');
      return;
    }

    setLoading(true);
    try {
      const expiresInDays = expiresIn ? parseInt(expiresIn) : undefined;
      const perms = selectedPermissions.size > 0 ? Array.from(selectedPermissions) : undefined;
      await onSubmit({
        name,
        description: description || undefined,
        role,
        expiresIn: expiresInDays,
        permissions: perms,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title="Create API Key"
      onClose={onClose}
      className="api-key-dialog"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            form="api-key-form"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Key'}
          </button>
        </>
      }
    >
        <form id="api-key-form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <span>Warning</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">API Key Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., React App, Mobile App, TC Track reputation"
              disabled={loading}
            />
            <small>A descriptive name for this API key</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What will this key be used for?"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
              disabled={loading}
            >
              <option value="user">User (standard permissions)</option>
              <option value="admin">Admin (full access)</option>
            </select>
            <small>Admin keys can manage settings, API keys, and more</small>
          </div>

          <div className="form-group">
            <label htmlFor="expiresIn">Expiration (optional)</label>
            <input
              id="expiresIn"
              type="number"
              min="1"
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value)}
              placeholder="Days until expiration"
              disabled={loading}
            />
            <small>Leave blank for no expiration</small>
          </div>

          {/* Advanced — explicit per-key permission grants */}
          <div className="form-group">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="advanced-toggle"
            >
              {showAdvanced ? 'Hide' : 'Show'} advanced permissions
            </button>
            {showAdvanced && (
              <div className="advanced-panel">
                <p>
                  Optionally restrict this key to a specific set of operations. Useful for
                  external integrations (e.g. a TC Track key that should ONLY be able to call
                  the reputation check endpoint). Leave nothing selected to use only the role
                  permissions above.
                </p>
                {permissionsLoading ? (
                  <div className="advanced-muted">Loading permissions...</div>
                ) : availablePermissions.length === 0 ? (
                  <div className="advanced-muted">
                    Permissions endpoint not available (may be a permission issue).
                  </div>
                ) : (
                  <>
                    {Object.entries(grouped).map(([category, perms]) => (
                      <div key={category} className="permission-group">
                        <div className="permission-group-title">
                          {category.replace(/_/g, ' ')}
                        </div>
                        {perms.map((p) => (
                          <div
                            key={p.key}
                            onClick={() => !loading && togglePermission(p.key)}
                            className={`permission-row ${loading ? 'disabled' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPermissions.has(p.key)}
                              onChange={() => togglePermission(p.key)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={loading}
                            />
                            <div>
                              <div className="permission-key">
                                {p.key}
                              </div>
                              <div className="permission-label">
                                {p.label}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    {selectedPermissions.size > 0 && (
                      <div className="permission-warning">
                        With explicit permissions set, this key can also perform those
                        operations in addition to whatever the <strong>{role}</strong> role
                        already grants. To create a strictly-scoped service key (e.g. a
                        reputation-only key), use <strong>role=user</strong> and select only
                        the specific permissions needed.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="alert alert-info">
            <span>Info</span>
            <span>
              The generated API key will only be shown once. Copy and store it securely!
            </span>
          </div>
        </form>
    </ModalShell>
  );
};

export default ApiKeyDialog;
